import React, { useState, useContext, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chip } from 'primereact/chip';
import { ToastContext } from '../../services/contexts/toast-context';
import {
  useGetRecruiterDashboard,
  useGetRecruiterProfile,
  useSearchProfiles,
  useUnlockProfile,
} from '../../services/api/recruiter-api';
import SearchFilters from './components/search-filters';
import CreditsDialog from './components/credits-dialog';
import Flex from '../../components/shared-styles/flexi';
import { useNavigate } from 'react-router-dom';
import { DashboardContainer , Header, SectionCard, TabBar, NoResults} from "./dashboard.styles"
import ReusableSidebar from '../../components/sidebar/reusable-sidebar';
import { useUser } from '../../services/contexts/user-context';
import { MainContent } from '../../components/sidebar/sidebar.styles';

const SEARCH_CACHE_KEY = 'recruiter_last_search';
const HIGHLIGHT_DURATION = 8000;
const DEFAULT_FILTERS = {
  skills: [],
  country: '',
  experienceLevel: '',
  minExperience: '',
  recentlyActive: false,
};

// Helper: Build search params from query and filters
const buildSearchParams = (query, filters) => {
  const params = {};
  if (query.trim()) params.search = query.trim();
  if (filters.skills.length) params.skills = filters.skills;
  if (filters.country) params.country = filters.country;
  if (filters.experienceLevel) params.experience_level = filters.experienceLevel;
  if (filters.minExperience) params.min_exp = filters.minExperience;
  if (filters.recentlyActive) params.recently_active = true;
  return params;
};

// Helper: Check if search has valid criteria
const hasSearchCriteria = (query, filters) => {
  return query.trim() || Object.values(filters).some(v => v || (Array.isArray(v) && v.length > 0));
};

export default function RecruiterDashboard() {
  const navigate = useNavigate();
  const toastRef = useContext(ToastContext);
  const { user, isLoading: userLoading } = useUser();
  const [isSidebarOpen, setIsSidebarOpen] = useState(() =>
    typeof window === 'undefined' || !window.matchMedia('(max-width: 768px)').matches
  );
  const hasRestoredSearch = useRef(false);

  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchParams, setSearchParams] = useState(null);

  // UI State
  const [activeTab, setActiveTab] = useState('search');
  const [showCreditsDialog, setShowCreditsDialog] = useState(false);
  const [highlightedProfileId, setHighlightedProfileId] = useState(null);

  // Guard: redirect to onboarding if no recruiter profile yet
  const { data: recruiterProfile, isLoading: profileLoading } = useGetRecruiterProfile();
  useEffect(() => {
    if (!profileLoading && !recruiterProfile) {
      navigate('/recruiter/on-boarding', { replace: true });
    }
  }, [profileLoading, recruiterProfile, navigate]);

  // API Hooks
  const { data: dashboardData, isLoading: dashboardLoading } = useGetRecruiterDashboard({
    enabled: !profileLoading && !!recruiterProfile,
  });
  const { data: searchResults = [], isFetching: isSearching } = useSearchProfiles(searchParams);
  const { mutate: unlockProfile, isPending: isUnlocking } = useUnlockProfile();

  // Unlocked Profiles State
  const [unlockedProfiles, setUnlockedProfiles] = useState([]);

  // Load unlocked profiles from dashboard
  useEffect(() => {
    if (dashboardData?.recent_unlocks) {
      setUnlockedProfiles(dashboardData.recent_unlocks);
    }
  }, [dashboardData]);

  // Restore last search from localStorage (only once on mount)
  useEffect(() => {
    if (hasRestoredSearch.current) return;
    hasRestoredSearch.current = true;

    const lastSearch = localStorage.getItem(SEARCH_CACHE_KEY);
    if (lastSearch) {
      try {
        const { searchQuery: lastQuery, filters: lastFilters } = JSON.parse(lastSearch);
        setSearchQuery(lastQuery);
        setFilters(lastFilters);
        setSearchParams(buildSearchParams(lastQuery, lastFilters));
      } catch (error) {
        console.error('Failed to restore search from localStorage:', error);
      }
    }
  }, []);

  // Handle highlight auto-removal
  useEffect(() => {
    if (highlightedProfileId) {
      const timer = setTimeout(() => setHighlightedProfileId(null), HIGHLIGHT_DURATION);
      return () => clearTimeout(timer);
    }
  }, [highlightedProfileId]);

  const handleSearch = () => {
    if (!hasSearchCriteria(searchQuery, filters)) {
      toastRef.current.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please enter a search term or select filters',
        life: 3000,
      });
      return;
    }

    const params = buildSearchParams(searchQuery, filters);
    setSearchParams(params);
    localStorage.setItem(SEARCH_CACHE_KEY, JSON.stringify({ searchQuery, filters }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleToggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleProfileUnlock = (profileId) => {
    // Check credits
    if (!dashboardData?.recruiter?.credits || dashboardData.recruiter.credits < 1) {
      setShowCreditsDialog(true);
      return;
    }

    unlockProfile(profileId, {
      onSuccess: (unlockedProfile) => {
        // Mark profile as unlocked in search results
        if (searchResults) {
          searchResults.forEach(p => {
            if (p.id === profileId) p.is_unlocked = true;
          });
        }

        setUnlockedProfiles(prev => [unlockedProfile, ...prev]);
        setActiveTab('unlocked');
        setHighlightedProfileId(profileId);

        toastRef.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Profile unlocked successfully!',
          life: 3000,
        });
      },
      onError: (error) => {
        toastRef.current.show({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Failed to unlock profile',
          life: 3000,
        });
      },
    });
  };

  const renderSkills = (rowData) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
      {rowData.skills?.slice(0, 5).map(skill => (
        <Chip 
          key={skill.id} 
          label={skill.name} 
          className="p-chip-sm" 
          style={{ fontSize: '0.8rem' }} 
        />
      ))}
      {rowData.skills?.length > 5 && (
        <Chip 
          label={`+${rowData.skills.length - 5} more`} 
          className="p-chip-sm p-chip-secondary" 
        />
      )}
    </div>
  );

  const renderExperience = (rowData) => `${rowData.experience_years || 0} yrs`;

  const renderAction = (rowData) => {
    const isUnlocked = rowData.is_unlocked || activeTab === 'unlocked';

    if (isUnlocked) {
      return (
        <Button
          label="View"
          icon="pi pi-eye"
          className="p-button-sm p-button-outlined p-button-primary"
          onClick={() => navigate(`/profile/${rowData.profile_name}`)}
        />
      );
    }

    return (
      <Button
        label="Unlock (1 Credit)"
        icon="pi pi-lock"
        className="p-button-sm p-button-primary"
        disabled={isUnlocking}
        loading={isUnlocking}
        onClick={() => handleProfileUnlock(rowData.id)}
      />
    );
  };

  // Loading state
  if (dashboardLoading || userLoading) {
    return <DashboardContainer>Loading...</DashboardContainer>;
  }

  // Calculate display data
  const tableData = activeTab === 'search' ? searchResults : unlockedProfiles;
  const tableTitle = activeTab === 'search' 
    ? `Search Results (${searchResults.length})` 
    : `Unlocked Profiles (${unlockedProfiles.length})`;

  return (
    <>
      <ReusableSidebar 
        isSidebarOpen={isSidebarOpen} 
        handleToggleSidebar={handleToggleSidebar} 
        selectedPage="dashboard"
        user={user}
        loading={userLoading}
      />
      
      <MainContent isSidebarOpen={isSidebarOpen}>
        <DashboardContainer>
          {/* Header with Credits */}
          <Header>
            <h1>Recruiter Dashboard</h1>
            <Flex alignItems='center'>
              <div style={{ marginRight: '1rem', color: 'var(--text-secondary)' }}>
                Credits: {dashboardData?.recruiter?.credits || 0}
              </div>
              <Button 
                label="Buy Credits" 
                icon="pi pi-credit-card" 
                className="p-button-primary" 
                onClick={() => navigate('/purchase')}
              />
            </Flex>
          </Header>

          {/* Search Filters */}
          <SectionCard>
            <SearchFilters 
              filters={filters} 
              onSearch={handleSearch} 
              onFilterChange={handleFilterChange} 
              isLoading={isSearching} 
            />
          </SectionCard>

          {/* Results Table */}
          <SectionCard>
            <TabBar>
              <Button 
                label="Search Results" 
                className={activeTab === 'search' ? 'p-button-secondary' : 'p-button-outlined'} 
                onClick={() => setActiveTab('search')} 
              />
              <Button 
                label="Unlocked Profiles" 
                className={activeTab === 'unlocked' ? 'p-button-secondary' : 'p-button-outlined'} 
                onClick={() => setActiveTab('unlocked')} 
              />
            </TabBar>

            <h2 style={{ textAlign: 'left' }}>{tableTitle}</h2>

            {tableData.length > 0 ? (
              <DataTable 
                value={tableData} 
                paginator 
                rows={10} 
                rowHover 
                emptyMessage="No profiles found"
                rowClassName={(rowData) => 
                  highlightedProfileId === rowData.id ? 'highlighted-row' : ''
                }
              >
                <Column field="profile_name" header="Name" sortable />
                <Column header="Experience" body={renderExperience} sortable />
                <Column header="Skills" body={renderSkills} />
                <Column field="country" header="Country" sortable />
                <Column header="Action" body={renderAction} />
              </DataTable>
            ) : (
              <NoResults>
                <i className="pi pi-search" style={{ fontSize: '3rem', marginBottom: '1rem' }}></i>
                <p>No profiles found. Try adjusting your search criteria.</p>
              </NoResults>
            )}
          </SectionCard>

          {/* Credits Dialog */}
          <CreditsDialog
            visible={showCreditsDialog}
            onHide={() => setShowCreditsDialog(false)}
            currentCredits={dashboardData?.recruiter?.credits}
          />
        </DashboardContainer>
      </MainContent>
    </>
  );
}
